import { createClient, Host } from '@wix/sdk';
import { reactElements, elements } from '@wix/editor';
import { ControlService } from '../../globalDesignPanel/types';

/**
 * Load externalised module '@wix/editor-elements-panel-service' with a fallback mechanism for different environments.
 */
async function importEditorElementsPanelService(): Promise<
  typeof import('@wix/editor-elements-panel-service')
> {
  try {
    // for environments where the service is extarnalised as a global variable
    // try regular externalised import first
    // e.g. 'Studio' & 'Editor 3' with module as `global window.WixEditorElementsPanelService` (https://wix.atlassian.net/browse/EP-6263)
    // TBD remove window.WixEditorElementsPanelService after all hosts support native esm import of @wix/editor-elements-panel-service
    // https://wix.slack.com/archives/CQGJP31CM/p1750182401867729?thread_ts=1749723888.974999&cid=CQGJP31CM
    const module =
      // @ts-expect-error
      window.WixEditorElementsPanelService ??
      // for environments where the service is NOT available globally, fallback to native es import
      // (`webpackIgnore: true` forces webpack to not bundle this import)
      // (important: module should be in es importMap of the host editor)
      // e.g. 'Santa Editor'
      (await import(
        /* webpackIgnore: true */
        '@wix/editor-elements-panel-service'
      ));

    if (!module) {
      throw new Error(
        `import result of '@wix/editor-elements-panel-service' is empty`,
      );
    }

    return module;
  } catch (error) {
    throw new Error(
      `Failed to import '@wix/editor-elements-panel-service' module`,
      {
        cause: error,
      },
    );
  }
}

export async function loadPanelService(host: Host) {
  if (!host) {
    throw new Error('Failed to load host');
  }

  const module = await importEditorElementsPanelService();
  const { createAutoPanelService } = module;

  const panelService = await createAutoPanelService({
    host,
    env: 'builder',
  });
  return panelService;
}

export async function loadControlService({
  host,
}: {
  host: Host;
}): Promise<ControlService> {
  try {
    const panelService = await loadPanelService(host);
    const client = createClient({
      host,
      modules: { reactElements, elements },
    });
    const styleDefinitions = await client.reactElements.getStyleDefinitions();
    const styles = await client.reactElements.getStyles();

    return {
      getControl: panelService.getControl,
      getControlGroup: panelService.getControlGroup,
      setStyles: client.reactElements.setStyles,
      styleDefinitions,
      styles,
    };
  } catch (error) {
    console.error(error);
    throw new Error('Control service not can not be loaded');
  }
}
